import * as pdfjsLib from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import JSZip from 'jszip';
import { Deck, DeckSlide } from './types';

// Served from public/, copied from node_modules/pdfjs-dist/build. Keep the two versions in sync.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const RENDER_WIDTH_PX = 1200;
const JPEG_QUALITY = 0.85;

export type ProgressFn = (done: number, total: number) => void;

export async function extractDeck(file: File, onProgress: ProgressFn): Promise<Deck> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) {
    return { fileName: file.name, kind: 'pdf', slides: await extractPdf(file, onProgress) };
  }
  if (name.endsWith('.pptx')) {
    return { fileName: file.name, kind: 'pptx', slides: await extractPptx(file, onProgress) };
  }
  throw new Error('Unsupported file type. Upload a .pdf or .pptx deck.');
}

async function extractPdf(file: File, onProgress: ProgressFn): Promise<DeckSlide[]> {
  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const slides: DeckSlide[] = [];

  try {
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);

      const content = await page.getTextContent();
      const text = content.items
        .filter((item): item is TextItem => 'str' in item)
        .map((item) => item.str + (item.hasEOL ? '\n' : ' '))
        .join('')
        .replace(/[ \t]+\n/g, '\n')
        .trim();

      const scale = RENDER_WIDTH_PX / page.getViewport({ scale: 1 }).width;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas is not available in this browser.');
      // JPEG has no alpha, so paint white first or transparent pages render black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;

      slides.push({ number: i, text, imageDataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY) });
      page.cleanup();
      onProgress(i, pdf.numPages);
    }
  } finally {
    pdf.destroy();
  }

  return slides;
}

const DRAWINGML_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

async function extractPptx(file: File, onProgress: ProgressFn): Promise<DeckSlide[]> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(await file.arrayBuffer());
  } catch {
    throw new Error('Could not open this .pptx file. It may be corrupted or password protected.');
  }

  const slidePaths = await slideOrder(zip);
  if (slidePaths.length === 0) throw new Error('No slides found in this .pptx file.');

  const parser = new DOMParser();
  const slides: DeckSlide[] = [];
  for (let i = 0; i < slidePaths.length; i++) {
    const xml = await zip.file(slidePaths[i])!.async('string');
    const doc = parser.parseFromString(xml, 'application/xml');
    // Each <a:p> is a paragraph; its <a:t> runs make up the visible text.
    const paragraphs = Array.from(doc.getElementsByTagNameNS(DRAWINGML_NS, 'p'))
      .map((p) =>
        Array.from(p.getElementsByTagNameNS(DRAWINGML_NS, 't'))
          .map((t) => t.textContent ?? '')
          .join(''),
      )
      .filter((line) => line.trim() !== '');
    slides.push({ number: i + 1, text: paragraphs.join('\n') });
    onProgress(i + 1, slidePaths.length);
  }
  return slides;
}

// Slide file names (slide1.xml, slide2.xml, ...) don't follow presentation order once slides are
// reordered, so read the order from presentation.xml and fall back to numeric file order.
async function slideOrder(zip: JSZip): Promise<string[]> {
  const fallback = Object.keys(zip.files)
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/.test(path))
    .sort((a, b) => slideNumber(a) - slideNumber(b));

  const presentation = await zip.file('ppt/presentation.xml')?.async('string');
  const rels = await zip.file('ppt/_rels/presentation.xml.rels')?.async('string');
  if (!presentation || !rels) return fallback;

  const parser = new DOMParser();
  const targets = new Map<string, string>();
  for (const rel of Array.from(parser.parseFromString(rels, 'application/xml').getElementsByTagName('Relationship'))) {
    const id = rel.getAttribute('Id');
    const target = rel.getAttribute('Target');
    if (id && target) targets.set(id, target.startsWith('/') ? target.slice(1) : `ppt/${target}`);
  }

  const ordered = Array.from(
    parser.parseFromString(presentation, 'application/xml').getElementsByTagName('p:sldId'),
  )
    .map((el) => targets.get(el.getAttributeNS(REL_NS, 'id') ?? ''))
    .filter((path): path is string => !!path && !!zip.file(path));

  return ordered.length > 0 ? ordered : fallback;
}

function slideNumber(path: string): number {
  return Number(path.match(/slide(\d+)\.xml$/)![1]);
}
